/* ---------------------------------------------------------------------------
   Convex binding — the ONLY file in the UI that names a backend function.

   Function names and argument shapes below were read off `convex/**` directly,
   so they are real, not guessed. What is still provisional is the TYPING:
   `convex/_generated/api` does not exist until `convex dev` has run, so this
   uses Convex's own `anyApi` proxy, which resolves `api.<module>.<fn>` by name
   at runtime with no codegen.

   RECONCILE (one line, no other file changes):
     import { api } from "../../convex/_generated/api";
   then delete `anyApi` and the `q`/`m`/`a` casts.
   --------------------------------------------------------------------------- */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import type { FunctionReference } from "convex/server";
import type { Phase, RoundCount, SegmentLength, SubChunk } from "../../convex/lib/game";
import type {
  Assignment,
  Auth,
  Clip,
  Player,
  RoomState,
  ScoreRow,
  SkipTally,
} from "./contracts";

export const api = anyApi;

type Args = Record<string, unknown>;

function q<T>(ref: unknown) {
  return ref as FunctionReference<"query", "public", Args, T>;
}
function m<T>(ref: unknown) {
  return ref as FunctionReference<"mutation", "public", Args, T>;
}
function a<T>(ref: unknown) {
  return ref as FunctionReference<"action", "public", Args, T>;
}

// --- Raw row shapes returned by convex/** -----------------------------------

interface RawRoomState {
  room: {
    _id: string;
    code: string;
    hostPlayerId?: string;
    hostConnected: boolean;
    controlOpen: boolean;
    phase: Phase;
    segmentLength: SegmentLength;
    roundCount: RoundCount;
    currentRound: number;
    playbackOrder: string[];
    playbackIndex: number;
    originalSkipped: boolean;
    soloTest: boolean;
  };
  players: Array<{
    _id: string;
    name: string;
    isHost: boolean;
    connected: boolean;
    score: number;
    needClip: boolean;
  }>;
}

interface RawClip {
  _id: string;
  ownerId: string;
  donatedBy?: string;
  r2Key: string;
  mimeType: string;
  durationSec: number;
  segmentStart: number;
  ready: boolean;
  dealable: boolean;
}

interface RawPoolStatus {
  coverage: Array<{ playerId: string; dealableCount: number; clipCount: number }>;
  waitingOn: string[];
}

interface RawAssignment {
  round: number;
  clip: { _id: string; r2Key: string; durationSec: number; segmentStart: number };
  subChunks: SubChunk[];
  redosRemaining: number;
  submitted: Array<{ subChunkIndex: number; r2Key: string; redoUsed: boolean }>;
}

interface RawDubEntry {
  playerId: string;
  name: string;
  clipId: string;
  chunks: Array<{ subChunkIndex: number; r2Key: string }>;
  complete: boolean;
}

interface RawVoteStatus {
  votesAllowed: number;
  voterIds: string[];
  complete: boolean;
  results: Array<{
    playerId: string;
    name: string;
    roundPoints: number;
    totalScore: number;
  }> | null;
}

interface RawSkipTally {
  votes: number;
  threshold: number;
  skipped: boolean;
  voterIds: string[];
}

// --- Function references ----------------------------------------------------

export const refs = {
  // rooms
  createRoom: m<{ roomId: string; code: string; playerId: string; token: string }>(
    api.rooms.createRoom,
  ),
  joinRoom: m<{ roomId: string; code: string; playerId: string; token: string }>(
    api.rooms.joinRoom,
  ),
  updateSettings: m<null>(api.rooms.updateSettings),
  startGame: m<{ phase: Phase; round: number }>(api.rooms.startGame),
  getState: q<RawRoomState | null>(api.rooms.getState),
  standings: q<Array<{ playerId: string; name: string; score: number }> | null>(
    api.rooms.standings,
  ),

  // players
  heartbeat: m<null>(api.players.heartbeat),
  setNeedClip: m<null>(api.players.setNeedClip),

  // clips
  requestClipUpload: a<{ clipId: string; r2Key: string; uploadUrl: string }>(
    api.clips.requestUpload,
  ),
  confirmUpload: m<null>(api.clips.confirmUpload),
  setSegmentStart: m<null>(api.clips.setSegmentStart),
  donateClip: m<{ clipId: string; to: string }>(api.clips.donateClip),
  listClips: q<RawClip[] | null>(api.clips.listByRoom),
  poolStatus: q<RawPoolStatus | null>(api.clips.poolStatus),

  // rounds
  dealRound: m<null>(api.rounds.dealRound),
  advancePhase: m<{ phase: Phase; round: number }>(api.rounds.advancePhase),
  setPlaybackIndex: m<{ playbackIndex: number }>(api.rounds.setPlaybackIndex),
  myAssignment: q<RawAssignment | null>(api.rounds.myAssignment),

  // dubs
  requestDubUpload: a<{ r2Key: string; uploadUrl: string }>(api.dubs.requestDubUpload),
  submitDub: m<null>(api.dubs.submitDub),
  useRedo: m<null>(api.dubs.useRedo),
  listDubs: q<RawDubEntry[] | null>(api.dubs.listForRound),

  // votes
  castVotes: m<null>(api.votes.castVotes),
  voteSkipOriginal: m<null>(api.votes.voteSkipOriginal),
  voteStatus: q<RawVoteStatus | null>(api.votes.status),
  skipTally: q<RawSkipTally | null>(api.votes.skipTally),

  // r2
  getSignedUrls: a<Array<{ key: string; url: string }>>(api.r2.getSignedUrls),
} as const;

// --- Room state -------------------------------------------------------------

/**
 * `rooms.getState`, `clips.listByRoom` and `clips.poolStatus` are three
 * subscriptions; the screens want one object. Stitching happens here so no
 * screen has to know the split.
 */
export function useRoomState(roomId: string | null): RoomState | null | undefined {
  const state = useQuery(refs.getState, roomId ? { roomId } : "skip");
  const clips = useQuery(refs.listClips, roomId ? { roomId } : "skip");
  const pool = useQuery(refs.poolStatus, roomId ? { roomId } : "skip");

  return useMemo(() => {
    if (!roomId) return undefined;
    if (state === null) return null;
    if (state === undefined || clips === undefined || pool === undefined) return undefined;

    const coverage = new Map(pool?.coverage.map((c) => [c.playerId, c]) ?? []);

    const players: Player[] = state.players.map((p) => ({
      playerId: p._id,
      name: p.name,
      isHost: p.isHost,
      connected: p.connected,
      needClip: p.needClip,
      covered: (coverage.get(p._id)?.dealableCount ?? 0) > 0,
      clipCount: coverage.get(p._id)?.clipCount ?? 0,
      score: p.score,
    }));

    const mapped: Clip[] = (clips ?? []).map((c) => ({
      clipId: c._id,
      ownerId: c.ownerId,
      donatedBy: c.donatedBy ?? null,
      r2Key: c.r2Key,
      mimeType: c.mimeType,
      durationSec: c.durationSec,
      segmentStart: c.segmentStart,
      ready: c.ready,
      dealable: c.dealable,
    }));

    return {
      roomId: state.room._id,
      code: state.room.code,
      phase: state.room.phase,
      hostPlayerId: state.room.hostPlayerId ?? null,
      hostConnected: state.room.hostConnected,
      controlOpen: state.room.controlOpen,
      round: state.room.currentRound,
      roundCount: state.room.roundCount,
      segmentLength: state.room.segmentLength,
      players,
      clips: mapped,
      playbackOrder: state.room.playbackOrder,
      playbackIndex: state.room.playbackIndex,
      originalSkipped: state.room.originalSkipped,
      soloTest: state.room.soloTest,
      waitingOn: pool?.waitingOn ?? [],
    };
  }, [roomId, state, clips, pool]);
}

// --- Signed URLs ------------------------------------------------------------

/**
 * `r2.getSignedUrls` is an action, not a query, so URLs cannot be subscribed
 * to — they are fetched once per key set and cached for this mount. Keys are
 * sorted into a stable cache token so a re-render does not re-sign.
 */
export function useSignedUrls(auth: Auth | null, keys: string[]): Record<string, string> {
  const getUrls = useAction(refs.getSignedUrls);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const cacheKey = useMemo(() => [...keys].sort().join("|"), [keys]);
  const fetched = useRef<string | null>(null);

  useEffect(() => {
    if (!auth || cacheKey.length === 0) return;
    if (fetched.current === cacheKey) return;
    fetched.current = cacheKey;

    let cancelled = false;
    void (async () => {
      try {
        const rows = await getUrls({
          playerId: auth.playerId,
          token: auth.token,
          keys: cacheKey.split("|"),
        });
        if (cancelled) return;
        setUrls((prev) => {
          const next = { ...prev };
          for (const row of rows) next[row.key] = row.url;
          return next;
        });
      } catch {
        // A stale signature is recoverable: the caller retries on the next
        // key change. Failing loudly here would spam the toast stack.
        fetched.current = null;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, cacheKey, getUrls]);

  return urls;
}

// --- Per-screen queries -----------------------------------------------------

export function useAssignment(auth: Auth | null): Assignment | null | undefined {
  const raw = useQuery(refs.myAssignment, auth ? { ...auth } : "skip");
  return useMemo(() => {
    if (raw === undefined) return undefined;
    if (raw === null) return null;
    return {
      round: raw.round,
      clipId: raw.clip._id,
      segmentStart: raw.clip.segmentStart,
      durationSec: raw.clip.durationSec,
      subChunks: raw.subChunks,
      recorded: raw.submitted.map((s) => s.subChunkIndex),
      redosRemaining: raw.redosRemaining,
    };
  }, [raw]);
}

/** The raw r2Key of the clip this player was dealt — needed to sign a URL. */
export function useAssignmentKey(auth: Auth | null): string | null {
  const raw = useQuery(refs.myAssignment, auth ? { ...auth } : "skip");
  return raw?.clip.r2Key ?? null;
}

export function useDubs(roomId: string | null, round: number) {
  return useQuery(refs.listDubs, roomId && round > 0 ? { roomId, round } : "skip");
}

export function useSkipTally(
  auth: Auth | null,
  roomId: string | null,
  round: number,
): SkipTally | undefined {
  const raw = useQuery(refs.skipTally, roomId && round > 0 ? { roomId, round } : "skip");
  return useMemo(() => {
    if (!raw || !auth) return undefined;
    return {
      votes: raw.votes,
      threshold: raw.threshold,
      skipped: raw.skipped,
      didVote: raw.voterIds.includes(auth.playerId),
    };
  }, [raw, auth]);
}

export function useVoteStatus(roomId: string | null, round: number) {
  return useQuery(refs.voteStatus, roomId && round > 0 ? { roomId, round } : "skip");
}

/** Round deltas + running totals. `results` is null until the reveal. */
export function useRoundScores(
  roomId: string | null,
  round: number,
): ScoreRow[] | undefined {
  const status = useVoteStatus(roomId, round);
  return useMemo(() => {
    if (!status?.results) return undefined;
    return status.results.map((r, i) => ({
      playerId: r.playerId,
      name: r.name,
      delta: r.roundPoints,
      total: r.totalScore,
      rank: i + 1,
    }));
  }, [status]);
}

export function useStandings(roomId: string | null): ScoreRow[] | undefined {
  const rows = useQuery(refs.standings, roomId ? { roomId } : "skip");
  return useMemo(() => {
    if (!rows) return undefined;
    return rows.map((r, i) => ({
      playerId: r.playerId,
      name: r.name,
      delta: 0,
      total: r.score,
      rank: i + 1,
    }));
  }, [rows]);
}

// --- Mutations / actions ----------------------------------------------------

export function useRoomActions() {
  const createRoom = useMutation(refs.createRoom);
  const joinRoom = useMutation(refs.joinRoom);
  const updateSettings = useMutation(refs.updateSettings);
  const startGame = useMutation(refs.startGame);

  return useMemo(
    () => ({
      create: (hostName: string, soloTest = false) => createRoom({ hostName, soloTest }),
      join: (code: string, name: string) => joinRoom({ code, name }),
      setSettings: (
        auth: Auth,
        patch: { segmentLength?: SegmentLength; roundCount?: RoundCount },
      ) => updateSettings({ ...auth, ...patch }),
      start: (auth: Auth) => startGame({ ...auth }),
    }),
    [createRoom, joinRoom, updateSettings, startGame],
  );
}

export function useClipActions() {
  const request = useAction(refs.requestClipUpload);
  const confirm = useMutation(refs.confirmUpload);
  const setSegment = useMutation(refs.setSegmentStart);
  const donate = useMutation(refs.donateClip);
  const setNeedClip = useMutation(refs.setNeedClip);

  return useMemo(
    () => ({
      request: (auth: Auth, file: { mimeType: string; durationSec: number; sizeBytes: number }) =>
        request({ ...auth, ...file }),
      confirm: (auth: Auth, clipId: string) => confirm({ ...auth, clipId }),
      setSegment: (auth: Auth, clipId: string, segmentStart: number) =>
        setSegment({ ...auth, clipId, segmentStart }),
      donate: (auth: Auth, clipId: string, toPlayerId: string) =>
        donate({ ...auth, clipId, toPlayerId }),
      setNeedClip: (auth: Auth, needClip: boolean) => setNeedClip({ ...auth, needClip }),
    }),
    [request, confirm, setSegment, donate, setNeedClip],
  );
}

export function useDubActions() {
  const request = useAction(refs.requestDubUpload);
  const submit = useMutation(refs.submitDub);
  const redo = useMutation(refs.useRedo);

  return useMemo(
    () => ({
      request: (auth: Auth, round: number, subChunkIndex: number, mimeType: string) =>
        request({ ...auth, round, subChunkIndex, mimeType }),
      submit: (auth: Auth, round: number, subChunkIndex: number, r2Key: string) =>
        submit({ ...auth, round, subChunkIndex, r2Key }),
      redo: (auth: Auth, round: number, subChunkIndex: number) =>
        redo({ ...auth, round, subChunkIndex }),
    }),
    [request, submit, redo],
  );
}

export function useVoteActions() {
  const cast = useMutation(refs.castVotes);
  const skip = useMutation(refs.voteSkipOriginal);

  return useMemo(
    () => ({
      cast: (auth: Auth, round: number, rankedPlayerIds: string[]) =>
        cast({ ...auth, round, rankedPlayerIds }),
      skipOriginal: (auth: Auth, round: number) => skip({ ...auth, round }),
    }),
    [cast, skip],
  );
}

export function usePhaseActions() {
  const deal = useMutation(refs.dealRound);
  const advance = useMutation(refs.advancePhase);
  const setPlaybackIndex = useMutation(refs.setPlaybackIndex);

  return useMemo(
    () => ({
      deal: (auth: Auth) => deal({ ...auth }),
      /**
       * `expectedPhase` is a compare-and-swap. Two players advancing at the
       * same instant would otherwise move the room two phases and skip one,
       * because advancing is a step rather than an absolute set.
       */
      advance: (auth: Auth, expectedPhase: Phase) =>
        advance({ ...auth, expectedPhase }),
      /** Host-only. Absolute index so a double-tap can't skip a dub. */
      setPlaybackIndex: (auth: Auth, index: number) =>
        setPlaybackIndex({ ...auth, index }),
    }),
    [deal, advance, setPlaybackIndex],
  );
}

/** Presence. A player who stops beating is skipped, not blocked on. */
export function useHeartbeat(auth: Auth | null) {
  const beat = useMutation(refs.heartbeat);
  const send = useCallback(() => {
    if (!auth) return;
    void beat({ ...auth }).catch(() => {});
  }, [auth, beat]);
  return send;
}
